import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import { TextField, Typography, Card, CardContent, InputAdornment, Grid } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Page, Text, View, Document, PDFViewer } from "@react-pdf/renderer";
import { useMaterialUIController } from "context";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import CancelIcon from "@mui/icons-material/Cancel";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import {
  FP,
  PageHeader,
  StatCard,
  fpDataGridSx,
  fpInputSx,
} from "components/financepro";
import { useApi, useSucursales } from "lib/api";

function formatMoneyCell(value) {
  if (value == null || value === "") return "0.00";
  return parseFloat(value).toFixed(2);
}

function buildTraspasoColumns() {
  return [
    { field: "id", headerName: "#Prest", width: 95, align: "center", headerAlign: "center" },
    { field: "fecha1", headerName: "Fecha", type: "string", width: 110, editable: true },
    { field: "nombres", headerName: "Nombres", flex: 1, minWidth: 130, editable: true },
    { field: "cedula", headerName: "Cedula", type: "string", width: 110, editable: true },
    {
      field: "chasis",
      headerName: "Chasis",
      sortable: false,
      flex: 1,
      minWidth: 160,
      resizable: true,
    },
    { field: "marca", headerName: "Marca", type: "string", flex: 0.85, minWidth: 115 },
    { field: "modelo", headerName: "Modelo", type: "string", flex: 0.85, minWidth: 115 },
    { field: "tnombres", headerName: "Traspasado a", type: "string", flex: 1, minWidth: 140 },
    { field: "tcedula", headerName: "Cedula", type: "string", width: 118 },
    {
      field: "valor",
      headerName: "Cobrado",
      type: "string",
      width: 118,
      align: "right",
      headerAlign: "right",
      valueGetter: (_value, row) => formatMoneyCell(row?.valor),
    },
  ];
}

function InformeTraspaso() {
  const [fecha1, setFecha1] = useState("");
  const [fecha2, setFecha2] = useState("");
  const [nosucursal, setNosucursal] = useState("");
  const [rows, setRows] = useState([]);
  const [presentarpdf, setPresentarpdf] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filtroNombre, setFiltroNombre] = useState("");

  const [controller] = useMaterialUIController();
  const { nombreNegocio } = controller;
  const { post } = useApi();
  const sucursal = useSucursales();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const rowsFiltrados = useMemo(() => {
    const q = (filtroNombre || "").trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const n = String(row.tnombres ?? "").toLowerCase();
      return n.includes(q);
    });
  }, [rows, filtroNombre]);

  const totalcobrado = rowsFiltrados.reduce(
    (sum, row) => sum + (parseFloat(row.valor) || 0),
    0
  );

  const columns = useMemo(() => buildTraspasoColumns(), []);

  async function procesarrequerimiento() {
    if (!fecha1 || !fecha2) {
      alert("Por favor seleccione ambas fechas");
      return;
    }
    setLoading(true);
    try {
      const { status, data } = await post("/api/informetraspaso", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        sucursal: nosucursal
          ? typeof nosucursal === "object" && nosucursal.label
            ? nosucursal.label.split("-")[0]
            : nosucursal
          : "",
      });
      if (status === 200) {
        setRows(data["matriculas"] || []);
        setFiltroNombre("");
        setLoading(false);
      } else if (status === 400) {
        setRows([]);
        alert(data["Error"]);
        setLoading(false);
      }
    } catch {
      setLoading(false);
      alert("Error al procesar la solicitud");
    }
  }

  const MyDocument = ({ rowsData, nombreNegocio: nombre }) => {
    const totalcobradoPDF = rowsData.reduce((sum, row) => sum + (parseFloat(row.valor) || 0), 0);
    return (
      <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%", colorScheme: "light", background: "#fff" }}>
        <Document>
          <Page size="A4" style={{ backgroundColor: "#FFFFFF" }}>
            <View style={{ textAlign: "center", width: "94%", fontSize: 14, textTransform: "uppercase", marginTop: "15px" }} fixed>
              <Text>{nombre || "Empresa"}</Text>
            </View>
            <View style={{ textAlign: "center", width: "94%", fontSize: 9, textTransform: "uppercase" }} fixed>
              <Text>Informe Traspaso</Text>
            </View>
            <View style={{ textAlign: "center", width: "94%", fontSize: 9, textTransform: "uppercase" }} fixed>
              <Text> </Text>
            </View>
            <View style={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "left", width: "94%", height: "25px", fontSize: 7, backgroundColor: "#1976d2", color: "white", marginLeft: "15px" }} fixed>
              <Text style={{ width: "60px", justifyContent: "left", fontWeight: "bold" }} fixed>Prest#</Text>
              <Text style={{ width: "80px", justifyContent: "left", fontWeight: "bold" }} fixed>Fecha</Text>
              <Text style={{ width: "80px", justifyContent: "left", fontWeight: "bold" }} fixed>Marca</Text>
              <Text style={{ width: "80px", justifyContent: "left", fontWeight: "bold" }} fixed>Modelo</Text>
              <Text style={{ width: "80px", justifyContent: "left", fontWeight: "bold" }} fixed>Chasis</Text>
              <Text style={{ width: "70px", justifyContent: "left", fontWeight: "bold" }} fixed>Cobrado</Text>
            </View>
            {rowsData.map((filas, i) => (
              <View key={i} style={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "left", width: "94%", height: "15px", fontSize: 6.5, color: "black", marginLeft: "15px", backgroundColor: i % 2 === 0 ? "#f9f9f9" : "white" }}>
                <Text style={{ width: "60px", justifyContent: "left" }} fixed>{filas.id}</Text>
                <Text style={{ width: "80px", justifyContent: "left" }} fixed>{filas.fecha}</Text>
                <Text style={{ width: "80px", justifyContent: "left" }} fixed>{filas.marca}</Text>
                <Text style={{ width: "80px", justifyContent: "left" }} fixed>{filas.modelo}</Text>
                <Text style={{ width: "80px", justifyContent: "left" }} fixed>{filas.chasis}</Text>
                <Text style={{ width: "70px", justifyContent: "left" }} fixed>{parseFloat(filas.valor || 0).toFixed(2)}</Text>
              </View>
            ))}
            <View style={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "left", width: "94%", height: "25px", fontSize: 8, backgroundColor: "#f5f5f5", color: "black", marginLeft: "15px", fontWeight: "bold", borderTop: "2px solid #1976d2" }}>
              <Text style={{ width: "60px", justifyContent: "left" }} fixed>Totales</Text>
              <Text style={{ width: "80px", justifyContent: "left" }} fixed></Text>
              <Text style={{ width: "80px", justifyContent: "left" }} fixed></Text>
              <Text style={{ width: "80px", justifyContent: "left" }} fixed></Text>
              <Text style={{ width: "80px", justifyContent: "left" }} fixed></Text>
              <Text style={{ width: "70px", justifyContent: "left" }} fixed>{totalcobradoPDF.toFixed(2)}</Text>
            </View>
          </Page>
        </Document>
      </PDFViewer>
    );
  };

  function handlepdf() {
    if (rowsFiltrados.length === 0) {
      alert("No hay datos para procesar este reporte");
    } else {
      setPresentarpdf(true);
    }
  }

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Motores"
        subtitle="Informe Traspaso"
        cta={
          <Button
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            disabled={rowsFiltrados.length === 0}
            onClick={handlepdf}
            sx={{
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
            }}
          >
            Export Data
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Filtros de Búsqueda
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Desde"
                  value={fecha1}
                  onChange={(event) => setFecha1(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Hasta"
                  value={fecha2}
                  onChange={(event) => setFecha2(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Autocomplete
                  disablePortal
                  options={sucursal || []}
                  value={nosucursal || null}
                  getOptionLabel={(opt) => (opt && opt.label) || ""}
                  isOptionEqualToValue={(opt, val) => (opt && val && opt.label) === (val && val.label)}
                  onChange={(event, newValue) => setNosucursal(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => procesarrequerimiento()}
                disabled={loading}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontSize: "0.94rem",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "#CBD5E1", color: "#FFFFFF" },
                }}
              >
                {loading ? "Procesando..." : "Buscar"}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <Box
            sx={{
              display: "grid",
              width: "100%",
              gap: 2,
              mb: 3,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <StatCard
              label="Total de registros"
              value={String(rowsFiltrados.length)}
              icon={DescriptionIcon}
              accent={FP.color.accentBlue}
            />
            <StatCard
              label="Total cobrado"
              value={`$${totalcobrado.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={AttachMoneyIcon}
              accent={FP.color.navy}
            />
          </Box>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Listado de traspasos
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
              {rows.length > 0 && (
                <TextField
                  size="small"
                  placeholder="Buscar por traspasado a"
                  value={filtroNombre}
                  onChange={(e) => setFiltroNombre(e.target.value)}
                  sx={{
                    minWidth: { xs: "100%", sm: 240 },
                    "& .MuiOutlinedInput-root": {
                      height: 36,
                      borderRadius: 1.2,
                      bgcolor: "#FFFFFF",
                      fontSize: "0.86rem",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {rowsFiltrados.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => handlepdf()}
                  sx={{
                    borderColor: FP.color.danger,
                    color: FP.color.danger,
                    borderRadius: 1.2,
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
                  }}
                >
                  Generar PDF
                </Button>
              )}
            </Box>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
            {rows.length > 0 ? (
              <>
                {rowsFiltrados.length === 0 && filtroNombre.trim() ? (
                  <Typography sx={{ color: FP.color.textSecondary, mb: 2, fontSize: "0.9rem" }}>
                    No hay registros que coincidan con el nombre de quien realizó el traspaso.
                  </Typography>
                ) : null}
                <Box sx={{ display: { xs: "none", md: "block" }, width: "100%", overflowX: "auto" }}>
                  <DataGrid
                    rows={rowsFiltrados}
                    columns={columns}
                    getRowId={(row) =>
                      row.id != null ? String(row.id) : `${row.chasis ?? ""}-${row.fecha1 ?? ""}`
                    }
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowHeight={42}
                    sx={{ minWidth: { xs: 900, sm: "100%" }, height: 520, ...fpDataGridSx }}
                    autoHeight={false}
                  />
                </Box>
                <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.4 }}>
                  {rowsFiltrados.map((row, idx) => (
                    <Box
                      key={row.id != null ? String(row.id) : `card-${idx}`}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: "#FFFFFF",
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1rem", lineHeight: 1.2 }}>
                            Préstamo #{row.id}
                          </Typography>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.78rem", mt: 0.25 }}>
                            {row.fecha1}
                          </Typography>
                        </Box>
                        <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                            Valor
                          </Typography>
                          <Typography sx={{ color: FP.color.textPrimary, fontSize: "1rem", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                            {row.valor ?? "—"}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                          Cliente original
                        </Typography>
                        <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.9rem", fontWeight: 600, wordBreak: "break-word" }}>
                          {row.nombres ?? "—"}
                        </Typography>
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem" }}>
                          {row.cedula ?? "—"}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 1, pt: 1, borderTop: `1px solid ${FP.color.border}` }}>
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                          Traspasado a
                        </Typography>
                        <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.9rem", fontWeight: 600, wordBreak: "break-word" }}>
                          {row.tnombres ?? "—"}
                        </Typography>
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem" }}>
                          {row.tcedula ?? "—"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 1 }}>
                        {[
                          ["Marca", row.marca],
                          ["Modelo", row.modelo],
                        ].map(([label, value]) => (
                          <Box key={label}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                              {label}
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.92rem", fontWeight: 600, wordBreak: "break-word" }}>
                              {value ?? "—"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      {row.chasis ? (
                        <Box sx={{ mt: 1 }}>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                            Chasis
                          </Typography>
                          <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.82rem", fontWeight: 600, wordBreak: "break-all" }}>
                            {row.chasis}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                  Seleccione los filtros y haga clic en &quot;Buscar&quot; para obtener resultados.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {presentarpdf && (
          <Dialog
            open={presentarpdf}
            maxWidth="lg"
            fullWidth
            fullScreen={isXs}
            onClose={() => setPresentarpdf(false)}
            PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
          >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight="bold">
                Vista Previa del Reporte PDF
              </Typography>
              <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: "auto", p: 1 }}>
                <CancelIcon />
              </Button>
            </DialogTitle>
            <DialogContent>
              <MyDocument rowsData={rowsFiltrados} nombreNegocio={nombreNegocio} />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </Box>
  );
}
export default InformeTraspaso;
